import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';

// 获取Firebase Functions实例
const app = getApp();
const functions = getFunctions(app, 'asia-southeast1');

// 获取所有占卜师数据（支持分页）- 使用Firebase Callable函数
export const getAllFortuneTellers = async (lastVisible = null, pageSize = 10, orderField = 'rating', orderDirection = 'desc') => {
  try {
    console.log(`开始获取占卜师数据: 页大小=${pageSize}, 排序字段=${orderField}, 排序方向=${orderDirection}`);
    
    // 准备调用参数
    const params = {
      status: 'active', 
      sortBy: orderField,
      sortDirection: orderDirection,
      limit: pageSize
    };
    
    // 如果有lastVisible，添加到参数中（尚未实现，后续需要完善）
    if (lastVisible) {
      // 暂不支持分页，可以在服务端函数中增加startAfter支持
      console.log('警告: 当前Firebase Functions不支持startAfter分页');
    }
    
    // 调用Firebase Callable函数
    const getFortuneTellersFn = httpsCallable(functions, 'getFortuneTellers');
    console.log('调用getFortuneTellers函数...');
    
    const result = await getFortuneTellersFn(params);
    console.log('getFortuneTellers函数调用结果:', result);
    
    if (result.data && result.data.success) {
      const fortuneTellers = result.data.fortuneTellers || [];
      console.log(`成功获取${fortuneTellers.length}个命理师数据`);
      
      // 由于Firebase Function目前不支持lastDoc，所以这里模拟一个lastDoc
      // 实际使用时可能需要修改服务端实现以支持分页
      const lastDoc = fortuneTellers.length > 0 ? { id: fortuneTellers[fortuneTellers.length - 1].id } : null;
      
      return {
        fortuneTellers,
        lastDoc
      };
    } else {
      console.error('获取命理师数据失败:', result.data?.message || '未知错误');
      throw new Error(result.data?.message || '获取命理师数据失败');
    }
  } catch (error) {
    console.error('获取占卜师数据失败:', error);
    
    // 对错误进行详细记录和处理
    if (error.code) {
      console.error('错误代码:', error.code);
    }
    if (error.message) {
      console.error('错误信息:', error.message);
    }
    if (error.details) {
      console.error('错误详情:', error.details);
    }
    
    // 抛出友好的错误消息
    throw new Error('获取命理师列表失败，请稍后重试');
  }
};

// 辅助函数：延迟函数，用于重试机制
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// 根据ID获取单个占卜师详情 - 直接从Firestore获取数据
export const getFortuneTellerById = async (id) => {
  try {
    console.log(`直接从Firestore获取命理师ID=${id}的详情`);
    
    if (!id) {
      console.error('占卜师ID为空');
      throw new Error('无效的占卜师ID');
    }
    
    // 直接从Firestore获取数据，绕过云函数
    console.log('直接从Firestore获取命理师数据...');
    
    const fortuneTellerRef = doc(db, 'fortuneTellers', id);
    const fortuneTellerDoc = await getDoc(fortuneTellerRef);
    
    if (!fortuneTellerDoc.exists()) {
      console.warn(`ID为${id}的占卜师不存在`);
      throw new Error('占卜师不存在');
    }
    
    const fortuneTellerData = fortuneTellerDoc.data();
    const fortuneTeller = {
      id: fortuneTellerDoc.id,
      ...fortuneTellerData
    };
    
    console.log(`成功直接从Firestore获取占卜师数据:`, fortuneTeller);
    
    // 确保数据包含所有必要字段
    return {
      id: fortuneTeller.id,
      name: fortuneTeller.name || '未命名占卜师',
      title: fortuneTeller.title || '占卜师',
      photoURL: fortuneTeller.photoURL || 'https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/avatar-default.jpg',
      rating: fortuneTeller.rating || 0,
      reviewCount: fortuneTeller.reviewCount || 0,
      price: fortuneTeller.price || 0,
      specialties: Array.isArray(fortuneTeller.specialties) ? fortuneTeller.specialties : [],
      featured: fortuneTeller.featured || false,
      experience: fortuneTeller.experience || 0,
      bio: fortuneTeller.bio || '暂无简介',
      availability: fortuneTeller.availability || {
        monday: [], tuesday: [], wednesday: [], thursday: [],
        friday: [], saturday: [], sunday: []
      },
      status: fortuneTellerData.status || 'active',
      ...fortuneTellerData  // 使用原始数据而不是处理过的数据
    };
  } catch (error) {
    console.error('获取占卜师详情失败:', error);
    
    // 对错误进行详细记录和处理
    if (error.code) {
      console.error('错误代码:', error.code);
    }
    if (error.message) {
      console.error('错误信息:', error.message);
    }
    if (error.details) {
      console.error('错误详情:', error.details);
    }
    
    // 抛出友好的错误信息
    if (error.message === '占卜师不存在') {
      throw new Error('该占卜师不存在或已被删除');
    } else if (error.message === '无效的占卜师ID') {
      throw new Error('请提供有效的占卜师ID');
    } else if (error.code === 'unavailable' || !navigator.onLine) {
      throw new Error('网络连接问题，无法获取占卜师数据。请检查您的网络连接后重试。');
    } else {
      throw new Error('获取占卜师信息失败，请稍后重试');
    }
  }
};

// 根据专长筛选占卜师 - 使用Firebase Callable函数
export const getFortuneTellersBySpecialty = async (specialty, lastVisible = null, pageSize = 10) => {
  try {
    console.log(`开始按专长筛选占卜师: 专长=${specialty}, 页大小=${pageSize}`);
    
    // 调用Firebase Callable函数
    const getFortuneTellersBySpecialtyFn = httpsCallable(functions, 'getFortuneTellersBySpecialty');
    console.log('调用getFortuneTellersBySpecialty函数...');
    
    const result = await getFortuneTellersBySpecialtyFn({ 
      specialty, 
      limit: pageSize 
    });
    
    console.log('getFortuneTellersBySpecialty函数调用结果:', result);
    
    if (result.data && result.data.success) {
      const fortuneTellers = result.data.fortuneTellers || [];
      console.log(`成功获取${fortuneTellers.length}个符合专长"${specialty}"的命理师数据`);
      
      // 由于Firebase Function目前不支持lastDoc，所以这里模拟一个lastDoc
      const lastDoc = fortuneTellers.length > 0 ? { id: fortuneTellers[fortuneTellers.length - 1].id } : null;
      
      return {
        fortuneTellers,
        lastDoc
      };
    } else {
      console.error('获取命理师数据失败:', result.data?.message || '未知错误');
      throw new Error(result.data?.message || '获取命理师数据失败');
    }
  } catch (error) {
    console.error('根据专长筛选占卜师失败:', error);
    
    // 对错误进行详细记录和处理
    if (error.code) {
      console.error('错误代码:', error.code);
    }
    if (error.message) {
      console.error('错误信息:', error.message);
    }
    if (error.details) {
      console.error('错误详情:', error.details);
    }
    
    // 抛出友好的错误消息
    throw new Error(`获取${specialty}专长命理师失败，请稍后重试`);
  }
};

// 根据评分范围筛选占卜师
export const getFortuneTellersByRating = async (minRating = 4.0, lastVisible = null, pageSize = 10) => {
  try {
    const fortuneTellersRef = collection(db, 'fortuneTellers');
    let q;

    if (lastVisible) {
      q = query(
        fortuneTellersRef, 
        where('status', '==', 'active'),
        where('rating', '>=', minRating),
        orderBy('rating', 'desc'),
        startAfter(lastVisible),
        limit(pageSize)
      );
    } else {
      q = query(
        fortuneTellersRef, 
        where('status', '==', 'active'),
        where('rating', '>=', minRating),
        orderBy('rating', 'desc'),
        limit(pageSize)
      );
    }

    const querySnapshot = await getDocs(q);
    
    const fortuneTellers = [];
    querySnapshot.forEach(doc => {
      fortuneTellers.push({
        id: doc.id,
        ...doc.data()
      });
    });

    const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
    return {
      fortuneTellers,
      lastDoc
    };
  } catch (error) {
    console.error('根据评分筛选占卜师失败:', error);
    throw error;
  }
};

// 根据价格范围筛选占卜师
export const getFortuneTellersByPriceRange = async (minPrice, maxPrice, lastVisible = null, pageSize = 10) => {
  try {
    const fortuneTellersRef = collection(db, 'fortuneTellers');
    let q;

    if (lastVisible) {
      q = query(
        fortuneTellersRef, 
        where('status', '==', 'active'),
        where('price', '>=', minPrice),
        where('price', '<=', maxPrice),
        orderBy('price', 'asc'),
        startAfter(lastVisible),
        limit(pageSize)
      );
    } else {
      q = query(
        fortuneTellersRef, 
        where('status', '==', 'active'),
        where('price', '>=', minPrice),
        where('price', '<=', maxPrice),
        orderBy('price', 'asc'),
        limit(pageSize)
      );
    }

    const querySnapshot = await getDocs(q);
    
    const fortuneTellers = [];
    querySnapshot.forEach(doc => {
      fortuneTellers.push({
        id: doc.id,
        ...doc.data()
      });
    });

    const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
    return {
      fortuneTellers,
      lastDoc
    };
  } catch (error) {
    console.error('根据价格筛选占卜师失败:', error);
    throw error;
  }
};

// 获取特荐占卜师 - 优化版，直接从Firestore获取
// 注意：此函数已在组件中直接实现，保留此函数以兼容旧代码
export const getFeaturedFortuneTellers = async (limit = 4) => {
  try {
    console.log(`开始获取特荐占卜师数据, 限制数量=${limit}`);
    
    // 使用已导入的db实例，确保一致性
    const fortuneTellersRef = collection(db, 'fortuneTellers');
    const featuredQuery = query(
      fortuneTellersRef,
      where('status', '==', 'active'),
      where('featured', '==', true),
      orderBy('rating', 'desc'),
      limit(limit)
    );
    
    const featuredSnapshot = await getDocs(featuredQuery);
    const fortuneTellers = featuredSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      featured: true, // 确保featured标记正确
      photoURL: doc.data().photoURL || 'https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/avatar-default.jpg'
    }));
    
    console.log(`成功获取${fortuneTellers.length}个特荐命理师数据`);
    return fortuneTellers;
  } catch (error) {
    console.error('获取特荐占卜师失败:', error);
    // 返回空数组而不是抛出错误
    return [];
  }
};

// 搜索占卜师 - 使用Firebase Callable函数
export const searchFortuneTellers = async (searchTerm) => {
  try {
    console.log(`开始搜索占卜师: 搜索词="${searchTerm}"`);
    
    if (!searchTerm || searchTerm.trim() === '') {
      console.warn('搜索词为空，返回空结果');
      return [];
    }
    
    // 调用Firebase Callable函数
    const searchFortuneTellersFn = httpsCallable(functions, 'searchFortuneTellers');
    console.log('调用searchFortuneTellers函数...');
    
    const result = await searchFortuneTellersFn({ 
      searchQuery: searchTerm.trim(), 
      limit: 20 
    });
    
    console.log('searchFortuneTellers函数调用结果:', result);
    
    if (result.data && result.data.success) {
      const fortuneTellers = result.data.fortuneTellers || [];
      console.log(`成功获取${fortuneTellers.length}个匹配搜索词"${searchTerm}"的命理师数据`);
      
      // 处理图片URL，确保所有占卜师有有效图片
      const processedFortuneTellers = fortuneTellers.map(teller => ({
        ...teller,
        photoURL: teller.photoURL || 'https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/avatar-default.jpg'
      }));
      
      return processedFortuneTellers;
    } else {
      console.error('搜索命理师失败:', result.data?.message || '未知错误');
      return [];
    }
  } catch (error) {
    console.error('搜索占卜师失败:', error);
    
    // 对错误进行详细记录和处理
    if (error.code) {
      console.error('错误代码:', error.code);
    }
    if (error.message) {
      console.error('错误信息:', error.message);
    }
    if (error.details) {
      console.error('错误详情:', error.details);
    }
    
    // 返回空数组而不是抛出错误，避免影响UI显示
    return [];
  }
};

// 获取可用时间段 - 直接从Firestore获取数据
export const getAvailableTimeSlots = async (fortuneTellerId, date) => {
  try {
    console.log(`直接从Firestore获取占卜师${fortuneTellerId}在${date.toLocaleDateString()}的可用时间段`);
    
    // 确保日期是有效的
    if (!date || isNaN(date.getTime())) {
      console.error('提供的日期无效:', date);
      throw new Error('无效的日期');
    }
    
    // 确保fortuneTellerId是有效的
    if (!fortuneTellerId) {
      console.error('占卜师ID为空');
      throw new Error('无效的占卜师ID');
    }
    
    // 获取星期几
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const weekday = days[date.getDay()];
    console.log('获取星期几:', weekday);
    
    // 从Firestore获取命理师文档
    const fortuneTellerRef = doc(db, 'fortuneTellers', fortuneTellerId);
    const fortuneTellerDoc = await getDoc(fortuneTellerRef);
    
    if (!fortuneTellerDoc.exists()) {
      console.warn(`ID为${fortuneTellerId}的占卜师不存在`);
      throw new Error('找不到指定的命理师');
    }
    
    const fortuneTeller = fortuneTellerDoc.data();
    
    // 获取命理师该日的可用时间
    const availability = fortuneTeller.availability || {};
    const availableTimes = availability[weekday] || [];
    console.log(`命理师在${weekday}的设置时间段:`, availableTimes);
    
    // 格式化日期为yyyy-MM-dd格式
    const formattedDate = date.toISOString().split('T')[0];
    console.log('格式化日期:', formattedDate);
    
    // 查询已有预约 - 只考虑已付款的预约
    const bookingsQuery = query(
      collection(db, 'bookings'),
      where('fortuneTellerId', '==', fortuneTellerId),
      where('formattedDate', '==', formattedDate),
      where('paymentStatus', '==', 'completed')
    );
    
    const bookingsSnapshot = await getDocs(bookingsQuery);
    
    // 整理已预约时间段
    const bookedTimes = [];
    bookingsSnapshot.forEach(doc => {
      const booking = doc.data();
      if (booking.timeSlot) { // 旧格式
        bookedTimes.push(booking.timeSlot);
      }
      if (booking.timeSlots && Array.isArray(booking.timeSlots)) { // 新格式
        bookedTimes.push(...booking.timeSlots);
      }
    });
    
    console.log('已预约时间段:', bookedTimes);
    
    // 过滤出可用时间段
    const availableTimeSlots = availableTimes.filter(time => !bookedTimes.includes(time));
    
    console.log(`命理师${fortuneTellerId}在${formattedDate}(${weekday})的可用时间段:`, availableTimeSlots);
    return availableTimeSlots;
  } catch (error) {
    console.error('获取可用时间段失败:', error);
    
    // 对错误进行详细记录和处理
    if (error.code) {
      console.error('错误代码:', error.code);
    }
    if (error.message) {
      console.error('错误信息:', error.message);
    }
    if (error.details) {
      console.error('错误详情:', error.details);
    }
    
    // 不使用默认时间段，返回空数组
    console.log('获取时间段出错，返回空数组');
    return [];
  }
};

// 创建预约 - 直接使用Firestore
export const createBooking = async (bookingData) => {
  try {
    console.log('开始直接在Firestore创建预约:', bookingData);
    
    // 必要参数验证
    if (!bookingData.fortuneTellerId) {
      throw new Error('命理师ID不能为空');
    }
    
    if (!bookingData.date) {
      throw new Error('预约日期不能为空');
    }
    
    if (!bookingData.timeSlots || !Array.isArray(bookingData.timeSlots) || bookingData.timeSlots.length === 0) {
      throw new Error('预约时间段不能为空');
    }
    
    if (!bookingData.userId) {
      throw new Error('用户ID不能为空');
    }
    
    // 确保日期格式正确
    const dateObj = bookingData.date instanceof Date ? bookingData.date : new Date(bookingData.date);
    const formattedDate = dateObj.toISOString().split('T')[0];
    
    // 检查时间段是否冲突 - 只与已付款的预约比较
    const bookingsQuery = query(
      collection(db, 'bookings'),
      where('fortuneTellerId', '==', bookingData.fortuneTellerId),
      where('formattedDate', '==', formattedDate),
      where('paymentStatus', '==', 'completed')
    );
    
    const bookingsSnapshot = await getDocs(bookingsQuery);
    
    // 整理已预约时间段
    const bookedTimes = [];
    bookingsSnapshot.forEach(doc => {
      const booking = doc.data();
      if (booking.timeSlot) {
        bookedTimes.push(booking.timeSlot);
      }
      if (booking.timeSlots && Array.isArray(booking.timeSlots)) {
        bookedTimes.push(...booking.timeSlots);
      }
    });
    
    // 检查是否有时间冲突
    const conflictedTimes = bookingData.timeSlots.filter(time => bookedTimes.includes(time));
    if (conflictedTimes.length > 0) {
      throw new Error(`所选时间段已被预约: ${conflictedTimes.join(', ')}`);
    }
    
    // 获取命理师信息获取价格
    const fortuneTellerRef = doc(db, 'fortuneTellers', bookingData.fortuneTellerId);
    const fortuneTellerDoc = await getDoc(fortuneTellerRef);
    
    if (!fortuneTellerDoc.exists()) {
      throw new Error('找不到指定的命理师');
    }
    
    const fortuneTeller = fortuneTellerDoc.data();
    
    // 创建预约数据
    const newBookingData = {
      userId: bookingData.userId,
      userName: bookingData.userName || 'Unknown User',
      fortuneTellerId: bookingData.fortuneTellerId,
      fortuneTellerName: fortuneTeller.name,
      date: serverTimestamp(), // 当前时间戳
      formattedDate,
      timeSlots: bookingData.timeSlots,
      notes: bookingData.notes || '',
      price: fortuneTeller.price * bookingData.timeSlots.length,
      status: 'pending',
      paymentStatus: 'pending',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    // 添加到Firestore
    const bookingRef = await addDoc(collection(db, 'bookings'), newBookingData);
    const bookingId = bookingRef.id;
    console.log('预约创建成功，ID:', bookingId);
    
    return bookingId;
  } catch (error) {
    console.error('创建预约失败:', error);
    
    // 对错误进行详细记录和处理
    if (error.code) {
      console.error('错误代码:', error.code);
    }
    if (error.message) {
      console.error('错误信息:', error.message);
    }
    if (error.details) {
      console.error('错误详情:', error.details);
    }
    
    // 抛出友好的错误消息
    if (error.message.includes('所选时间段已被预约')) {
      throw new Error(error.message);
    } else if (error.message === '找不到指定的命理师') {
      throw new Error('找不到指定的命理师，请刷新页面后重试');
    } else if (error.code === 'permission-denied') {
      throw new Error('您没有权限创建预约，请确保已登录');
    } else {
      throw new Error('创建预约失败，请稍后重试');
    }
  }
};

// 创建占卜师函数应该被移除，保留签名用于兼容性但抛出错误
export const createSampleFortuneTellers = async () => {
  console.error('创建示例占卜师功能已被禁用');
  throw new Error('不允许创建示例数据，请使用后台管理界面添加真实数据');
};

// 辅助函数：将日期转换为星期几的键名
export const getWeekdayKey = (date) => {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[date.getDay()];
};

// 辅助函数：转换星期几为中文显示
export const getDayName = (day) => {
  const days = {
    monday: '周一',
    tuesday: '周二',
    wednesday: '周三',
    thursday: '周四',
    friday: '周五',
    saturday: '周六',
    sunday: '周日'
  };
  return days[day] || day;
};

export default {
  getAllFortuneTellers,
  getFortuneTellerById,
  getFortuneTellersBySpecialty,
  getFortuneTellersByRating,
  getFortuneTellersByPriceRange,
  getFeaturedFortuneTellers,
  searchFortuneTellers,
  getAvailableTimeSlots,
  createBooking,
  getWeekdayKey,
  getDayName
};